/**
 * The hunt settings' ranges and the worst-case wait, for the Settings tab
 * (docs/plans/oncall-line-plan.md 2.3, 7.7; WP-11).
 *
 * A TypeScript port of the backend's own numbers, so the page shows exactly
 * what the call path will do:
 *
 *   SETTING_LIMITS      backend/src/services/oncall/settings.js SETTING_LIMITS
 *   clampSettingInt     settings.js clampInt: not a finite number -> the
 *                       default; a fraction is rounded; outside the range ->
 *                       the nearest bound
 *   wavesPerPass,
 *   totalWaves,
 *   worstCaseSeconds    backend/src/services/oncall/hunt.js, the same names
 *                       and the same answers (plan 16, WP-04: "WP-11 ports
 *                       exactly this formula")
 *
 * worstCaseSeconds = waves x passes x (ring_seconds + SCREEN_SECONDS), where a
 * pass is ceil(members / wave_size) waves and SCREEN_SECONDS (8) is the time a
 * wave may spend in the press-1 screen after its ring. 12 members in pairs at
 * 20 s is 6 x 28 = 168 s ("about 2 3/4 minutes"); one at a time is 336 s. The
 * greeting and the hold messages add a few seconds and are not counted. With
 * nobody on the list it is 0 (the call goes to the fallback numbers).
 *
 * __tests__/worstCase.test.ts runs this against the table of
 * backend/tests/unit/oncall/hunt.test.js; a change to either formula must
 * change both.
 */

export interface IntLimit {
  min: number;
  max: number;
  default: number;
}

/** The integer settings of 2.3 and their ranges (settings.js SETTING_LIMITS). */
export const SETTING_LIMITS = Object.freeze({
  ring_seconds: Object.freeze({ min: 10, max: 40, default: 20 }),
  wave_size: Object.freeze({ min: 1, max: 10, default: 2 }),
  max_passes: Object.freeze({ min: 1, max: 2, default: 1 }),
  hold_message_every: Object.freeze({ min: 0, max: 10, default: 3 }),
  callback_window_days: Object.freeze({ min: 1, max: 90, default: 30 }),
}) as Readonly<Record<IntSettingKey, Readonly<IntLimit>>>;

export type IntSettingKey = 'ring_seconds' | 'wave_size' | 'max_passes' | 'hold_message_every' | 'callback_window_days';

/** Seconds a wave may spend screening after its ring (hunt.js SCREEN_SECONDS; 2.3 "up to ~8 s"). */
export const SCREEN_SECONDS = 8;

/** At most this many alert addresses (settings.js MAX_ALERT_EMAILS). */
export const MAX_ALERT_EMAILS = 10;

/** The longest a spoken text may be (settings.js MAX_TEXT_LENGTH). */
export const MAX_TEXT_LENGTH = 1000;

/** The longest a line name may be (line.js MAX_LINE_NAME_LENGTH). */
export const MAX_LINE_NAME_LENGTH = 100;

/** The longest the fallback note may be (line.js MAX_FALLBACK_NOTE_LENGTH). */
export const MAX_FALLBACK_NOTE_LENGTH = 2000;

/** settings.js clampInt: never throws; a bad value takes the default. */
export function clampSettingInt(value: unknown, limit: IntLimit): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return limit.default;
  const n = Math.round(value);
  if (n < limit.min) return limit.min;
  if (n > limit.max) return limit.max;
  return n;
}

/** hunt.js nonNegativeInt: a non-negative safe integer (a string of digits is accepted), or null. */
function nonNegativeInt(value: unknown): number | null {
  const n = typeof value === 'string' && /^\d+$/.test(value.trim()) ? Number(value.trim()) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 ? n : null;
}

/** Waves in one pass through the members (hunt.js wavesPerPass). */
export function wavesPerPass(memberCount: unknown, waveSize: unknown): number {
  const n = nonNegativeInt(memberCount) || 0;
  if (n === 0) return 0;
  const size = clampSettingInt(waveSize, SETTING_LIMITS.wave_size);
  return Math.ceil(n / size);
}

/** Every wave of the hunt, all passes (hunt.js totalWaves). */
export function totalWaves(memberCount: unknown, waveSize: unknown, maxPasses: unknown): number {
  return wavesPerPass(memberCount, waveSize) * clampSettingInt(maxPasses, SETTING_LIMITS.max_passes);
}

/** The settings worstCaseSeconds reads; anything else in the object is ignored. */
export interface HuntTiming {
  ring_seconds?: unknown;
  wave_size?: unknown;
  max_passes?: unknown;
  [other: string]: unknown;
}

/**
 * The longest a caller can wait before voicemail, in seconds (hunt.js
 * worstCaseSeconds): raw or clamped settings (a missing or bad value takes
 * its default, as clampSettings does) and the count of active members.
 */
export function worstCaseSeconds(settings: HuntTiming | null | undefined, memberCount: unknown): number {
  const s = settings && typeof settings === 'object' && !Array.isArray(settings) ? settings : {};
  const ring = clampSettingInt(s.ring_seconds, SETTING_LIMITS.ring_seconds);
  const waveSize = clampSettingInt(s.wave_size, SETTING_LIMITS.wave_size);
  const maxPasses = clampSettingInt(s.max_passes, SETTING_LIMITS.max_passes);
  const waves = totalWaves(memberCount, waveSize, maxPasses);
  return waves * (ring + SCREEN_SECONDS);
}
